/**
 * アプリの「新バージョン検知」と「確実なリロード」を提供する。
 *
 * 背景:
 * PWA（ホーム画面追加）として使っている端末、とくに iOS スタンドアロンでは
 *  - アプリを復帰させても自動で読み直さない
 *  - ブラウザの更新ボタン・引っ張って更新が無い
 *  といった理由から、サーバーへ新バージョンをデプロイしても古い画面のまま
 *  取り残されることがある。Service Worker の内部実装（キャッシュ戦略）が
 *  分からない環境でも確実に更新できるよう、ここでは Service Worker に依存せず、
 *  「配信中の HTML が参照するビルド資産（ハッシュ付き JS）が変わったか」で
 *  新バージョンを判定し、キャッシュを避けたリロードで更新する。
 *
 * 注意: Readdy プレビュー（__IS_PREVIEW__ = true）では検知処理を行わない。
 */

/** アプリの基準 URL（ベースパスを考慮）。 */
function appRootUrl(): string {
  const base = typeof __BASE_PATH__ === 'string' && __BASE_PATH__ ? __BASE_PATH__ : '/';
  if (typeof window === 'undefined') return base;
  return new URL(base, window.location.origin).toString();
}

/** ビルド資産のパスを正規化して比較できるようにする（origin を落とす）。 */
function normalizeSrc(src: string): string {
  if (!src) return '';
  try {
    return new URL(src, typeof window !== 'undefined' ? window.location.origin : 'https://local').pathname;
  } catch {
    return src;
  }
}

/** 対象ドキュメント内の <script type="module" src> 群から、アプリのエントリを選ぶ。 */
function pickEntryScript(scripts: Array<{ src: string }>): string | null {
  if (scripts.length === 0) return null;
  const byAsset = scripts.find((s) => /\/assets\//.test(s.src));
  if (byAsset) return byAsset.src;
  const byMain = scripts.find((s) => /main\.(t|j)sx?($|\?)/.test(s.src) || /\/src\/main/.test(s.src));
  if (byMain) return byMain.src;
  return scripts[scripts.length - 1].src;
}

/** 現在表示中のページが読み込んだエントリ JS の URL を取得する。 */
function getCurrentEntrySrc(): string | null {
  if (typeof document === 'undefined') return null;
  const nodes = Array.from(
    document.querySelectorAll<HTMLScriptElement>('script[type="module"][src]'),
  );
  return pickEntryScript(nodes.map((n) => ({ src: n.getAttribute('src') || n.src })));
}

/**
 * デプロイ済みの最新 HTML を取得し、参照しているエントリ JS が現在のものと違うかを返す。
 * 取得失敗・プレビュー環境では false（＝更新なし扱い）を返し、誤検知を避ける。
 */
export async function hasNewVersion(): Promise<boolean> {
  if (__IS_PREVIEW__) return false;
  if (typeof window === 'undefined' || typeof fetch !== 'function') return false;

  const current = getCurrentEntrySrc();
  if (!current) return false;

  try {
    // キャッシュを避けるため、クエリを付けて no-store で取得する。
    const res = await fetch(`${appRootUrl()}?_v=${Date.now()}`, {
      cache: 'no-store',
      headers: { 'Cache-Control': 'no-cache' },
    });
    if (!res.ok) return false;
    const html = await res.text();

    const doc = new DOMParser().parseFromString(html, 'text/html');
    const nodes = Array.from(doc.querySelectorAll<HTMLScriptElement>('script[type="module"][src]'));
    const latest = pickEntryScript(nodes.map((n) => ({ src: n.getAttribute('src') || '' })));
    if (!latest) return false;

    return normalizeSrc(latest) !== normalizeSrc(current);
  } catch {
    return false;
  }
}

/**
 * iOS スタンドアロンでも効く「確実なリロード」。
 * location.reload() が効かないことがあるため、キャッシュを避けたクエリを付けて
 * 同一パスへ replace 遷移し、新しい HTML（＝新しい資産）を取り直させる。
 */
export function reloadApp(): void {
  if (typeof window === 'undefined') return;
  try {
    const url = new URL(window.location.href);
    url.searchParams.set('_v', Date.now().toString());
    window.location.replace(url.toString());
  } catch {
    window.location.reload();
  }
}

/**
 * 更新を適用する。待機中の Service Worker があれば有効化を促したうえで
 * （対応していればの話。未対応でも無害）、確実なリロードを行う。
 */
export async function applyAppUpdate(): Promise<void> {
  try {
    if (typeof navigator !== 'undefined' && 'serviceWorker' in navigator) {
      const reg = await navigator.serviceWorker.getRegistration();
      if (reg?.waiting) {
        reg.waiting.postMessage({ type: 'SKIP_WAITING' });
        // 新しい SW が有効化されるのを少し待つ（待てなくてもリロードは実行する）。
        await new Promise((resolve) => {
          window.setTimeout(resolve, 300);
        });
      }
    }
  } catch {
    /* 失敗してもリロードは実行する */
  }
  reloadApp();
}